import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import { api } from '../api';
import { toast } from 'sonner';
import {
  Wrench, Plus, Check, Clock, AlertTriangle, Trash2, Shield,
  Server, FileCheck, Settings2, Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import PageLayout from '../components/PageLayout';

const CATEGORY_ICONS = {
  security: Shield,
  infrastructure: Server,
  compliance: FileCheck,
  operations: Settings2,
  custom: Zap,
};

const CATEGORY_COLORS = {
  security: "bg-red-50 text-red-700 border-red-200",
  infrastructure: "bg-blue-50 text-blue-700 border-blue-200",
  compliance: "bg-purple-50 text-purple-700 border-purple-200",
  operations: "bg-green-50 text-green-700 border-green-200",
  custom: "bg-slate-50 text-slate-700 border-slate-200",
};

const MaintenancePage = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAssigned, setFilterAssigned] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ title: '', description: '', frequency: 'quarterly', category: 'custom', assigned_to: '' });
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [tasksRes, usersRes] = await Promise.all([
        api.get('/maintenance-tasks'),
        api.get('/users'),
      ]);
      setTasks(tasksRes.data || []);
      const adminUsers = (usersRes.data || []).filter(u => u.role === 'admin' || u.role === 'partial_admin');
      setAdmins(adminUsers);
    } catch {
      toast.error('Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleComplete = async (taskId) => {
    try {
      const res = await api.put(`/maintenance-tasks/${taskId}/complete`);
      toast.success(`Completed — next due ${new Date(res.data.next_due).toLocaleDateString()}`);
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const handleSnooze = async (taskId) => {
    try {
      await api.put(`/maintenance-tasks/${taskId}/snooze`);
      toast.success('Snoozed for 7 days');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await api.delete(`/maintenance-tasks/${taskId}`);
      toast.success('Deleted');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const handleInitSystem = async () => {
    try {
      const res = await api.post('/maintenance-tasks/init-system', { assigned_to: user?.user_id });
      toast.success(`${res.data.created} system tasks created`);
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const handleCreate = async () => {
    if (!addForm.title) { toast.error('Title required'); return; }
    setSubmitting(true);
    try {
      await api.post('/maintenance-tasks', {
        ...addForm,
        assigned_to: addForm.assigned_to || user?.user_id,
      });
      toast.success('Task created');
      setShowAdd(false);
      setAddForm({ title: '', description: '', frequency: 'quarterly', category: 'custom', assigned_to: '' });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setSubmitting(false);
    }
  };

  const now = new Date().toISOString();
  const soon = new Date(Date.now() + 14 * 86400000).toISOString();

  const getDueStatus = (dueDate) => {
    if (!dueDate) return 'ok';
    if (dueDate < now) return 'overdue';
    if (dueDate < soon) return 'upcoming';
    return 'ok';
  };

  const filtered = tasks.filter(t => {
    if (filterAssigned !== 'all' && t.assigned_to !== filterAssigned) return false;
    if (filterCategory !== 'all' && t.category !== filterCategory) return false;
    return true;
  });

  const hasSystemTasks = tasks.some(t => t.is_system);

  const headerActions = (
    <>
      {!hasSystemTasks && (
        <Button variant="outline" size="sm" className="rounded-full h-8 text-xs" onClick={handleInitSystem} data-testid="init-system-btn">
          <Zap className="w-3.5 h-3.5 mr-1.5" />Setup System Tasks
        </Button>
      )}
      <Button className="rounded-full bg-rojo hover:bg-rojo-dark h-9" onClick={() => { setAddForm({ ...addForm, assigned_to: user?.user_id }); setShowAdd(true); }} data-testid="add-task-btn">
        <Plus className="w-4 h-4 mr-1.5" />Add Task
      </Button>
    </>
  );

  return (
    <PageLayout title="Maintenance Tasks" headerSub="Recurring admin housekeeping reminders" headerActions={headerActions} maxWidth="max-w-4xl">
      <div className="space-y-5">
        {/* Summary cards */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="p-3 text-center">
            <p className="text-2xl font-bold text-red-600">{tasks.filter(t => getDueStatus(t.due_date) === 'overdue').length}</p>
            <p className="text-xs text-slate-500">Overdue</p>
          </Card>
          <Card className="p-3 text-center">
            <p className="text-2xl font-bold text-amber-600">{tasks.filter(t => getDueStatus(t.due_date) === 'upcoming').length}</p>
            <p className="text-xs text-slate-500">Due Soon</p>
          </Card>
          <Card className="p-3 text-center">
            <p className="text-2xl font-bold text-green-600">{tasks.filter(t => getDueStatus(t.due_date) === 'ok').length}</p>
            <p className="text-xs text-slate-500">On Track</p>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2">
          <Select value={filterAssigned} onValueChange={setFilterAssigned}>
            <SelectTrigger className="w-48 h-9" data-testid="filter-assigned">
              <SelectValue placeholder="All Admins" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Admins</SelectItem>
              {admins.map(a => (
                <SelectItem key={a.user_id} value={a.user_id}>{a.name || a.email}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-40 h-9" data-testid="filter-category">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="security">Security</SelectItem>
              <SelectItem value="infrastructure">Infrastructure</SelectItem>
              <SelectItem value="compliance">Compliance</SelectItem>
              <SelectItem value="operations">Operations</SelectItem>
              <SelectItem value="custom">Custom</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Task list */}
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Wrench className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No maintenance tasks</p>
              <p className="text-sm text-slate-400 mt-1">
                {!hasSystemTasks ? 'Click "Setup System Tasks" to get started with recommended recurring checks.' : 'All caught up!'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {filtered.map(task => {
              const status = getDueStatus(task.due_date);
              const Icon = CATEGORY_ICONS[task.category] || Wrench;
              const colorClass = CATEGORY_COLORS[task.category] || "";
              const dueDate = task.due_date ? new Date(task.due_date) : null;

              return (
                <Card key={task.task_id} className={`${status === 'overdue' ? 'border-red-200 bg-red-50/30' : status === 'upcoming' ? 'border-amber-200 bg-amber-50/20' : ''}`}>
                  <CardContent className="py-3">
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${status === 'overdue' ? 'bg-red-100' : status === 'upcoming' ? 'bg-amber-100' : 'bg-slate-100'}`}>
                        {status === 'overdue' ? <AlertTriangle className="w-4 h-4 text-red-600" /> : <Icon className="w-4 h-4 text-slate-600" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-slate-800">{task.title}</p>
                          <Badge variant="outline" className={`text-xs ${colorClass}`}>{task.category}</Badge>
                          <Badge variant="outline" className="text-xs">{task.frequency}</Badge>
                          {task.is_system && <Badge className="bg-primary/10 text-primary border-0 text-xs">System</Badge>}
                        </div>
                        {task.description && <p className="text-sm text-slate-500 mt-0.5">{task.description}</p>}
                        <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-400">
                          <span className={status === 'overdue' ? 'text-red-600 font-medium' : status === 'upcoming' ? 'text-amber-600' : ''}>
                            {status === 'overdue' ? 'Overdue' : 'Due'}: {dueDate?.toLocaleDateString()}
                          </span>
                          <span>→ {task.assigned_to_name || 'Unassigned'}</span>
                          {task.last_completed && <span>Last done: {new Date(task.last_completed).toLocaleDateString()}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-green-600" title="Mark complete" onClick={() => handleComplete(task.task_id)}>
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-amber-500" title="Snooze 7 days" onClick={() => handleSnooze(task.task_id)}>
                          <Clock className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400" title="Delete" onClick={() => handleDelete(task.task_id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Task Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">New Maintenance Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input value={addForm.title} onChange={(e) => setAddForm({ ...addForm, title: e.target.value })} placeholder="e.g., Review SSL certificates" data-testid="task-title" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={addForm.description} onChange={(e) => setAddForm({ ...addForm, description: e.target.value })} placeholder="What needs to be done and how" rows={3} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Frequency</Label>
                <Select value={addForm.frequency} onValueChange={(v) => setAddForm({ ...addForm, frequency: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="annually">Annually</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Category</Label>
                <Select value={addForm.category} onValueChange={(v) => setAddForm({ ...addForm, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="security">Security</SelectItem>
                    <SelectItem value="infrastructure">Infrastructure</SelectItem>
                    <SelectItem value="compliance">Compliance</SelectItem>
                    <SelectItem value="operations">Operations</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Assign To</Label>
              <Select value={addForm.assigned_to} onValueChange={(v) => setAddForm({ ...addForm, assigned_to: v })}>
                <SelectTrigger data-testid="task-assigned-to"><SelectValue placeholder="Select admin..." /></SelectTrigger>
                <SelectContent>
                  {admins.map(a => (
                    <SelectItem key={a.user_id} value={a.user_id}>{a.name || a.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button className="bg-rojo hover:bg-rojo-dark" onClick={handleCreate} disabled={submitting || !addForm.title} data-testid="save-task-btn">
              {submitting ? 'Creating...' : 'Create Task'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default MaintenancePage;
